import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Download, Send } from "lucide-react";
import { portalApi } from "@/lib/portalApi";

const inputCls = "w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E] transition-colors";

const TRACK_LABELS = {
  "vessels": "Vessels",
  "vows-and-vessels": "Vows & Vessels",
  "reflection-nights": "Reflection Nights",
  "the-circle": "The Circle",
  "retreats": "Retreats",
  "reflections-book": "Reflections (Book)",
};

export default function AdminPage() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_track: {} });
  const [drafts, setDrafts] = useState([]);
  const [clubMembers, setClubMembers] = useState([]);
  const [trials, setTrials] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [invite, setInvite] = useState({ subject: "", message: "", event_date: "", location: "", audience: "all" });
  const [sending, setSending] = useState(false);
  const [broadcast, setBroadcast] = useState({ audience: "club_all", subject: "", message: "" });
  const [sendingMass, setSendingMass] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [appsOpen, setAppsOpen] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = localStorage.getItem("op_portal_token");
    if (!t) { navigate("/portal/login"); return; }
    portalApi.get("/auth/me")
      .then((r) => {
        if (r.data?.role !== "admin") { navigate("/portal"); return; }
        setAuthed(true);
      })
      .catch(() => { localStorage.removeItem("op_portal_token"); navigate("/portal/login"); });
  }, [navigate]);

  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const [e, s, d, cm, tr, rn, st] = await Promise.all([
          portalApi.get(`/waitlist`),
          portalApi.get(`/waitlist/stats`),
          portalApi.get(`/application/drafts`),
          portalApi.get(`/club-members`),
          portalApi.get(`/trial-signups`),
          portalApi.get(`/reflection-night-attendees`),
          portalApi.get(`/settings`),
        ]);
        setEntries(e.data);
        setStats(s.data);
        setDrafts(d.data);
        setClubMembers(cm.data);
        setTrials(tr.data);
        setAttendees(rn.data);
        setAppsOpen(!!st.data.cohort_applications_open);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) { navigate("/portal/login"); return; }
        console.error(err);
      } finally { setLoading(false); }
    })();
  }, [authed, navigate]);

  const TIER_LABELS = { club_women_monthly: "Women · $20/mo", club_couples_monthly: "Couples · $45/mo" };

  const sendInvite = async () => {
    if (!invite.subject.trim() || !invite.message.trim()) { toast.error("Add a subject and a message."); return; }
    setSending(true);
    try {
      const { data } = await portalApi.post(`/club-invite`, invite);
      toast.success(`Invite sent to ${data.sent} of ${data.total} members.`);
      setInvite({ subject: "", message: "", event_date: "", location: "", audience: "all" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally { setSending(false); }
  };

  const BROADCAST_AUDIENCES = [
    ["club_all", "All Club members"],
    ["club_women_monthly", "Club · Women"],
    ["club_couples_monthly", "Club · Couples"],
    ["trials", "Free trial sign-ups"],
    ["waitlist_all", "All applicants (waitlist)"],
    ["waitlist:vessels", "Applicants · VESSELS"],
    ["waitlist:vows-and-vessels", "Applicants · Vows & Vessels"],
    ["waitlist:reflection-nights", "List · Reflection Nights"],
    ["waitlist:the-circle", "List · The Circle"],
    ["waitlist:retreats", "List · Retreats"],
  ];

  const sendMass = async () => {
    if (!broadcast.subject.trim() || !broadcast.message.trim()) { toast.error("Add a subject and a message."); return; }
    setSendingMass(true);
    try {
      const { data } = await portalApi.post(`/mass-email`, broadcast);
      toast.success(`Email sent to ${data.sent} of ${data.total} recipients.`);
      setBroadcast({ ...broadcast, subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally { setSendingMass(false); }
  };

  const submittedEmails = new Set(entries.map((e) => `${(e.email || "").toLowerCase()}|${e.track}`));
  const openDrafts = drafts.filter((d) => !submittedEmails.has(`${(d.email || "").toLowerCase()}|${d.track}`));

  const trackOrder = ["vessels", "vows-and-vessels", "reflection-nights", "the-circle", "retreats", "reflections-book"];

  const exportCsv = () => {
    if (!entries.length) return;
    const answerKeys = Array.from(
      entries.reduce((set, e) => { Object.keys(e.answers || {}).forEach((k) => set.add(k)); return set; }, new Set())
    );
    const headers = ["Name", "Email", "Track", "Reference", "Submitted", ...answerKeys];
    const esc = (v) => {
      if (v == null) return "";
      const s = Array.isArray(v) ? v.join("; ") : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = entries.map((e) => [
      e.name || "", e.email || "", TRACK_LABELS[e.track] || e.track, e.ref || "",
      new Date(e.created_at).toLocaleString(),
      ...answerKeys.map((k) => (e.answers || {})[k]),
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outpoured-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportAttendeesCsv = () => {
    if (!attendees.length) return;
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Name", "Email", "Date", "Booked", "Paid"];
    const rows = attendees.map((a) => [
      a.name || "", a.email || "", a.date_label || "",
      a.created_at ? new Date(a.created_at).toLocaleString() : "",
      a.paid ? "Yes" : "No",
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reflection-night-attendees-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleApplications = async () => {
    setSavingToggle(true);
    try {
      const { data } = await portalApi.post(`/admin/settings`, { cohort_applications_open: !appsOpen });
      setAppsOpen(!!data.cohort_applications_open);
      toast.success(data.cohort_applications_open ? "Applications are now OPEN." : "Applications are now CLOSED (waitlist mode).");
    } catch (err) {
      toast.error("Couldn't update. Try again.");
    } finally { setSavingToggle(false); }
  };

  if (!authed) return <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center text-[#8a7a6a] font-body">Verifying access…</div>;

  return (
    <div className="min-h-screen bg-[#F4EFE6] text-[#2A2825] py-16 px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">Outpoured · admin</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h1 className="font-serif-display text-4xl md:text-5xl font-light">Signups &amp; Applications</h1>
          <button
            data-testid="admin-export-csv"
            onClick={exportCsv}
            disabled={!entries.length}
            className="inline-flex items-center gap-2 rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-6 py-3 text-sm tracking-wide transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" strokeWidth={1.5} /> Export CSV
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-[#D1C9BE] border border-[#D1C9BE] mb-12">
          <div data-testid="stat-total" className="bg-[#F4EFE6] p-6">
            <p className="text-[10px] tracking-overline uppercase text-[#8a7a6a] mb-2">Total</p>
            <p className="font-serif-display text-3xl text-[#2A2825]">{stats.total}</p>
          </div>
          {trackOrder.map((t) => (
            <div key={t} data-testid={`stat-${t}`} className="bg-[#F4EFE6] p-6">
              <p className="text-[10px] tracking-overline uppercase text-[#8a7a6a] mb-2">{TRACK_LABELS[t]}</p>
              <p className="font-serif-display text-3xl text-[#2A2825]">{stats.by_track?.[t] ?? 0}</p>
            </div>
          ))}
        </div>

        {openDrafts.length > 0 && (
          <div data-testid="admin-drafts" className="mb-12">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#2A2825]">Started but unfinished</h2>
              <span className="text-sm font-body text-[#8a7a6a]">{openDrafts.length} in progress</span>
            </div>
            <p className="text-sm text-[#8a7a6a] font-body mb-5 max-w-2xl">People who began an application but haven't submitted yet. A gentle reminder is sent automatically in the week before the November 15 deadline.</p>
            <div className="border border-[#D8CFC0] bg-[#EAE3D9]/30">
              {openDrafts.map((d, i) => (
                <div key={`${d.email}-${d.track}-${i}`} data-testid="draft-row" className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-body items-center border-b border-[#D1C9BE]/50 last:border-0">
                  <span className="col-span-3 text-[#2A2825]">{d.name || "—"}</span>
                  <span className="col-span-4 text-[#5E4B3C] truncate">{d.email}</span>
                  <span className="col-span-2 text-[#A9724E]">{TRACK_LABELS[d.track] || d.track}</span>
                  <span className="col-span-2 text-[#8a7a6a]">{d.updated_at ? new Date(d.updated_at).toLocaleDateString() : "—"}</span>
                  <span className="col-span-1 text-right">
                    {d.reminded ? <span className="text-[10px] tracking-overline uppercase text-[#A9724E]">Nudged</span> : <span className="text-[10px] tracking-overline uppercase text-[#8a7a6a]">Waiting</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-[#5E4B3C]">Loading…</p>
        ) : entries.length === 0 ? (
          <p data-testid="admin-empty" className="text-[#5E4B3C]">No signups yet.</p>
        ) : (
          <div className="border border-[#D1C9BE]" data-testid="admin-table">
            {entries.map((e) => {
              const hasAnswers = e.answers && Object.keys(e.answers).length > 0;
              const isOpen = openId === e.id;
              return (
                <div key={e.id} data-testid="admin-row" className="border-b border-[#D1C9BE]/60 last:border-0">
                  <div
                    className={`grid grid-cols-12 gap-4 px-6 py-4 text-sm font-body items-center ${hasAnswers ? "cursor-pointer hover:bg-[#EAE3D9]/40" : ""}`}
                    onClick={() => hasAnswers && setOpenId(isOpen ? null : e.id)}
                  >
                    <span className="col-span-3 text-[#2A2825]">{e.name || "—"}</span>
                    <span className="col-span-4 text-[#5E4B3C] truncate">{e.email}</span>
                    <span className="col-span-3 text-[#A9724E]">{TRACK_LABELS[e.track] || e.track}</span>
                    <span className="col-span-2 flex items-center justify-between text-[#8a7a6a]">
                      {new Date(e.created_at).toLocaleDateString()}
                      {hasAnswers && <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />}
                    </span>
                  </div>
                  {hasAnswers && isOpen && (
                    <div data-testid="admin-answers" className="px-6 pb-6 pt-1 bg-[#EAE3D9]/30 space-y-4">
                      {Object.entries(e.answers).map(([k, v]) => (
                        <div key={k}>
                          <p className="text-[10px] tracking-overline uppercase text-[#8a7a6a] mb-1">{k.replace(/_/g, " ")}</p>
                          <p className="text-sm text-[#2A2825] font-body leading-relaxed">{v || "—"}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* CLUB MEMBERS */}
        <div data-testid="admin-club-members" className="mt-16">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#2A2825]">Club members</h2>
            <span className="text-sm font-body text-[#8a7a6a]">{clubMembers.length} active</span>
          </div>
          {clubMembers.length === 0 ? (
            <p className="text-[#8a7a6a] font-body text-sm">No Club members yet.</p>
          ) : (
            <div className="border border-[#D1C9BE]">
              {clubMembers.map((m, i) => (
                <div key={`${m.email}-${i}`} data-testid="club-member-row" className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-body items-center border-b border-[#D1C9BE]/60 last:border-0">
                  <span className="col-span-3 text-[#2A2825]">{m.name || "—"}</span>
                  <span className="col-span-4 text-[#5E4B3C] truncate">{m.email}</span>
                  <span className="col-span-3 text-[#A9724E]">{TIER_LABELS[m.tier] || m.tier_label || m.tier}</span>
                  <span className="col-span-2 text-[#8a7a6a]">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* TRIAL SIGNUPS */}
        <div data-testid="admin-trials" className="mt-16">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#2A2825]">Free trial sign-ups</h2>
            <span className="text-sm font-body text-[#8a7a6a]">{trials.length} total</span>
          </div>
          {trials.length === 0 ? (
            <p className="text-[#8a7a6a] font-body text-sm">No trial sign-ups yet.</p>
          ) : (
            <div className="border border-[#D1C9BE]">
              {trials.map((t, i) => (
                <div key={`${t.email}-${i}`} data-testid="trial-row" className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-body items-center border-b border-[#D1C9BE]/60 last:border-0">
                  <span className="col-span-4 text-[#2A2825]">{t.name || "—"}</span>
                  <span className="col-span-5 text-[#5E4B3C] truncate">{t.email}</span>
                  <span className="col-span-3 text-[#8a7a6a]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* REFLECTION NIGHT ATTENDEES */}
        <div data-testid="admin-rn-attendees" className="mt-16">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#2A2825]">Reflection Morning attendees · Oct 23</h2>
            <div className="flex items-center gap-4">
              <span className="text-sm font-body text-[#8a7a6a]">{attendees.length} reserved</span>
              <button data-testid="rn-export-csv" onClick={exportAttendeesCsv} disabled={!attendees.length}
                className="inline-flex items-center gap-2 rounded-full border border-[#2A2825]/30 px-4 py-2 text-xs tracking-wide text-[#2A2825] hover:bg-[#2A2825] hover:text-[#F4EFE6] disabled:opacity-40 transition-colors">
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Download CSV
              </button>
            </div>
          </div>
          {attendees.length === 0 ? (
            <p className="text-[#8a7a6a] font-body text-sm">No seats reserved yet.</p>
          ) : (
            <div className="border border-[#D1C9BE]">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 text-[11px] tracking-overline uppercase text-[#8a7a6a] border-b border-[#D1C9BE]/60 bg-[#EAE3D9]/40">
                <span className="col-span-3">Name</span>
                <span className="col-span-4">Email</span>
                <span className="col-span-2">Seat</span>
                <span className="col-span-3">Booked</span>
              </div>
              {attendees.map((a, i) => (
                <div key={`${a.email}-${i}`} data-testid="rn-attendee-row" className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-body items-center border-b border-[#D1C9BE]/60 last:border-0">
                  <span className="col-span-3 text-[#2A2825]">{a.name || "—"}</span>
                  <span className="col-span-4 text-[#5E4B3C] truncate">{a.email}</span>
                  <span className="col-span-2" data-testid="rn-attendee-seat">{a.paid ? <span className="text-xs rounded-full bg-[#2A2825] text-[#F4EFE6] px-2.5 py-1">Paid $27</span> : <span className="text-xs rounded-full border border-[#A9724E] text-[#A9724E] px-2.5 py-1">Free · {a.comp_code || "code"}</span>}</span>
                  <span className="col-span-3 text-[#8a7a6a]">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* COHORT APPLICATIONS TOGGLE */}
        <div data-testid="admin-apps-toggle" className="mt-16 border border-[#D1C9BE] bg-[#EAE3D9]/40 rounded-lg p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-serif-display text-2xl font-light text-[#2A2825] mb-1">Cohort applications</h2>
              <p className="text-sm font-body text-[#5E4B3C] max-w-md">When open, the Cohorts page shows the "Apply" buttons. When closed, it hides them and shows the "notify me when applications open" waitlist instead.</p>
            </div>
            <div className="flex items-center gap-4">
              <span data-testid="apps-status" className={`text-sm font-body font-medium ${appsOpen ? "text-[#5c8a5c]" : "text-[#a9724e]"}`}>{appsOpen ? "OPEN · Apply now" : "CLOSED · Waitlist"}</span>
              <button data-testid="apps-toggle-btn" onClick={toggleApplications} disabled={savingToggle} role="switch" aria-checked={appsOpen}
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${appsOpen ? "bg-[#5c8a5c]" : "bg-[#c7b8a6]"} disabled:opacity-50`}>
                <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${appsOpen ? "translate-x-7" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* CLUB INVITE COMPOSER */}
        <div data-testid="admin-club-invite" className="mt-16 mb-8">
          <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#2A2825] mb-2">Invite the Club</h2>
          <p className="text-sm text-[#8a7a6a] font-body mb-6 max-w-2xl">Send an email invite to your Club members for a local in-person or online activity. It goes to everyone active in the tier you choose.</p>
          <div className="border border-[#D1C9BE] bg-[#EAE3D9]/30 p-6 space-y-4 max-w-2xl">
            <input data-testid="invite-subject" className={inputCls} placeholder="Subject (e.g. You're invited: September Gathering)" value={invite.subject} onChange={(e) => setInvite({ ...invite, subject: e.target.value })} />
            <textarea data-testid="invite-message" rows={5} className={inputCls} placeholder="Write your invitation…" value={invite.message} onChange={(e) => setInvite({ ...invite, message: e.target.value })} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="invite-date" className={inputCls} placeholder="When (e.g. Sept 27, 2026 · 6pm)" value={invite.event_date} onChange={(e) => setInvite({ ...invite, event_date: e.target.value })} />
              <input data-testid="invite-location" className={inputCls} placeholder="Where (address or Zoom link)" value={invite.location} onChange={(e) => setInvite({ ...invite, location: e.target.value })} />
            </div>
            <select data-testid="invite-audience" className={inputCls} value={invite.audience} onChange={(e) => setInvite({ ...invite, audience: e.target.value })}>
              <option value="all">All Club members</option>
              <option value="club_women_monthly">Women only</option>
              <option value="club_couples_monthly">Couples only</option>
            </select>
            <button data-testid="invite-send" onClick={sendInvite} disabled={sending}
              className="inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-3 text-sm tracking-wide transition-colors duration-500 disabled:opacity-50">
              <Send className="w-4 h-4" strokeWidth={1.5} />{sending ? "Sending…" : "Send invite"}
            </button>
          </div>
        </div>

        {/* MASS EMAIL / BROADCAST */}
        <div data-testid="admin-broadcast" className="mt-16 mb-8">
          <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#2A2825] mb-2">Send a mass email</h2>
          <p className="text-sm text-[#8a7a6a] font-body mb-6 max-w-2xl">Write a custom email to any group — Club members, trial sign-ups, or applicants by track.</p>
          <div className="border border-[#D1C9BE] bg-[#EAE3D9]/30 p-6 space-y-4 max-w-2xl">
            <select data-testid="broadcast-audience" className={inputCls} value={broadcast.audience} onChange={(e) => setBroadcast({ ...broadcast, audience: e.target.value })}>
              {BROADCAST_AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input data-testid="broadcast-subject" className={inputCls} placeholder="Subject" value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} />
            <textarea data-testid="broadcast-message" rows={6} className={inputCls} placeholder="Write your message…" value={broadcast.message} onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })} />
            <button data-testid="broadcast-send" onClick={sendMass} disabled={sendingMass}
              className="inline-flex items-center gap-2 rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-6 py-3 text-sm tracking-wide transition-colors duration-500 disabled:opacity-50">
              <Send className="w-4 h-4" strokeWidth={1.5} />{sendingMass ? "Sending…" : "Send mass email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
